import styled from 'styled-components';

import { BoldRenderer } from '../plugins/bold/components/bold';
import { ItalicRenderer } from '../plugins/italic/components/italic';
import { UnderlineRenderer } from '../plugins/underline/components/underline';
import { ParagraphRenderer } from '../plugins/paragraph/components/paragraph';
import {
  InlineBookmarkRenderer,
  ReadonlyInlineBookmarkRenderer,
} from '../plugins/inlineBookmark/components/InlineBookmark';
import { OrderedListRenderer, UnorderedListRenderer } from '../plugins/list/components/List';
import { PicklistRenderer } from '../plugins/picklist/components/PicklistElement';
import {
  PlaceholderReadOnlyRenderer,
  PlaceholderRenderElement as PlaceholderRenderer,
} from '../plugins/placeholder/components/Placeholder';
import { HighlightedRenderer } from '../plugins/highlighted/components/highlighted';
import { HeadingRenderer } from '../plugins/heading/components/heading';
import { LineBreakRenderer } from '../plugins/lineBreak/components/lineBreak';
import { TextSourceStylingRenderer } from '../plugins/textSourceStyling/components/TextSourceStyling';
import { HeadingErrorTextRenderer } from '../plugins/headingError/components/headingError';
import type { ElementPlugin } from '../plugins/types';
import { ElementComponent } from './Element';
import { unreachableCaseLog } from 'types';
import type { RendererRenderElement, RendererRenderText } from './types';
import { DeepLinkRenderer, ReadonlyDeepLinkRenderer } from '../plugins/deepLink/components/index';
import type { HeadingStyle, Styles } from 'generated/graphql';
import { getDefaultReporterStyles } from 'hooks/useReporterStyles';
import { LIST_VARIANTS } from '../plugins/list/constants';
import { DICTATION_ID_TEXT_MARK, DICTATION_INDEX_TEXT_MARK } from '../plugins';
import { useEffect, useMemo, type CSSProperties } from 'react';
import { isHeadingLikeParagraph } from '../plugins/headingDecoration/sectionHelpers';
import { ReadonlyHeadingLikeParagraph } from '../plugins/headingDecoration/components/readonlyHeadingParagraph';

export type RendererProps = Readonly<{
  content: ElementPlugin[];
  renderElement?: RendererRenderElement;
  renderText?: RendererRenderText;
  style?: Styles;
  className?: string;
  isReadOnly?: boolean;
  // RP-6070 (M2b): when supplied (headings-as-decoration on), heading-like paragraphs whose
  // leading run is a recognised section label render as headings. Absent = today's behaviour.
  headingLabelSet?: Set<string>;
  onContentRendered?: () => void;
  onContentUnmounted?: () => void;
}>;

const RendererContainer = styled.div``;

const defaultRenderText: RendererRenderText = ({ mark, children, leaf, text, attributes }) => {
  switch (mark) {
    case 'bold':
      return <BoldRenderer>{children}</BoldRenderer>;
    case 'italic':
      return <ItalicRenderer>{children}</ItalicRenderer>;
    case 'underline':
      return <UnderlineRenderer>{children}</UnderlineRenderer>;
    case 'highlighted':
      return <HighlightedRenderer>{children}</HighlightedRenderer>;
    case 'headingError':
      return <HeadingErrorTextRenderer>{children}</HeadingErrorTextRenderer>;
    case 'source':
      return (
        <TextSourceStylingRenderer attributes={attributes} leaf={leaf} text={text}>
          {children}
        </TextSourceStylingRenderer>
      );
    case DICTATION_ID_TEXT_MARK:
    case DICTATION_INDEX_TEXT_MARK:
      // dictationId and dictationIndex marks are internal only and should not be rendered
      return children;
    default:
      unreachableCaseLog(mark);
      return children;
  }
};

const defaultRenderElement: RendererRenderElement = ({ children, node }) => {
  switch (node.type) {
    case 'inlineBookmark':
      return <InlineBookmarkRenderer element={node}>{children}</InlineBookmarkRenderer>;
    case 'paragraph':
      return <ParagraphRenderer element={node}>{children}</ParagraphRenderer>;
    case 'list':
      if (node.variant === LIST_VARIANTS.ol) {
        return <OrderedListRenderer>{children}</OrderedListRenderer>;
      } else if (node.variant === LIST_VARIANTS.ul) {
        return <UnorderedListRenderer>{children}</UnorderedListRenderer>;
      } else return null;
    case 'picklist':
      return <PicklistRenderer element={node}>{children}</PicklistRenderer>;
    case 'placeholder':
      return <PlaceholderRenderer element={node}>{children}</PlaceholderRenderer>;
    case 'deepLink':
      return <DeepLinkRenderer element={node}>{children}</DeepLinkRenderer>;
    case 'heading':
      return <HeadingRenderer element={node}>{children}</HeadingRenderer>;
    case 'lineBreak':
      return <LineBreakRenderer>{children}</LineBreakRenderer>;
    default:
      unreachableCaseLog(node.type);
      return null;
  }
};

export const readOnlyRenderElement: RendererRenderElement = ({ children, node }) => {
  switch (node.type) {
    case 'inlineBookmark':
      return (
        <ReadonlyInlineBookmarkRenderer element={node} leftDelimiter="" rightDelimiter="">
          {children}
        </ReadonlyInlineBookmarkRenderer>
      );
    case 'paragraph':
      return <ParagraphRenderer element={node}>{children}</ParagraphRenderer>;
    case 'list':
      if (node.variant === LIST_VARIANTS.ol) {
        return <OrderedListRenderer>{children}</OrderedListRenderer>;
      } else if (node.variant === LIST_VARIANTS.ul) {
        return <UnorderedListRenderer>{children}</UnorderedListRenderer>;
      } else return null;
    case 'picklist':
      return (
        <PicklistRenderer leftDelimiter="" rightDelimiter="" element={node}>
          {children}
        </PicklistRenderer>
      );
    case 'placeholder':
      return <PlaceholderReadOnlyRenderer element={node}>{children}</PlaceholderReadOnlyRenderer>;
    case 'deepLink':
      return <ReadonlyDeepLinkRenderer element={node}>{children}</ReadonlyDeepLinkRenderer>;
    case 'heading':
      return <HeadingRenderer element={node}>{children}</HeadingRenderer>;
    case 'lineBreak':
      return <LineBreakRenderer>{children}</LineBreakRenderer>;
    default:
      unreachableCaseLog(node.type);
      return null;
  }
};

// RP-6070 (M2b): wrap a base render function so heading-like paragraphs render as headings
// when a label set is supplied. Module-level so it's a named render fn (not an inline component).
const createHeadingAwareRenderElement = ({
  base,
  headingLabelSet,
  headingStyles,
  fontFamily,
}: {
  base: RendererRenderElement;
  headingLabelSet?: Set<string>;
  headingStyles: ReadonlyArray<HeadingStyle>;
  fontFamily?: string;
}): RendererRenderElement => {
  if (headingLabelSet == null) return base;
  const renderElement: RendererRenderElement = (props) => {
    if (isHeadingLikeParagraph(props.node, headingLabelSet)) {
      return (
        <ReadonlyHeadingLikeParagraph headingStyles={headingStyles} fontFamily={fontFamily}>
          {props.children}
        </ReadonlyHeadingLikeParagraph>
      );
    }
    return base(props);
  };
  return renderElement;
};

export const Renderer = ({
  content,
  renderElement,
  renderText = defaultRenderText,
  style = getDefaultReporterStyles(),
  isReadOnly = false,
  headingLabelSet,
  onContentRendered,
  onContentUnmounted,
  ...rest
}: RendererProps): React.ReactElement => {
  const baseRenderElement =
    renderElement ?? (isReadOnly ? readOnlyRenderElement : defaultRenderElement);
  // Delegates everything else to the base renderer (byte-identical when no label set).
  const effectiveRenderElement = useMemo<RendererRenderElement>(
    () =>
      createHeadingAwareRenderElement({
        base: baseRenderElement,
        headingLabelSet,
        headingStyles: style?.headingStyles ?? [],
        fontFamily: style?.bodyStyle?.fontFamily ?? undefined,
      }),
    [headingLabelSet, baseRenderElement, style]
  );
  useEffect(() => {
    if (onContentRendered == null) {
      return;
    }

    onContentRendered();
    return () => onContentUnmounted?.();
  }, [onContentRendered, onContentUnmounted]);

  const cssProperties: CSSProperties = {
    fontFamily: style?.bodyStyle?.fontFamily ?? undefined,
    fontSize: style?.bodyStyle?.fontSize ?? undefined,
    textTransform: style?.bodyStyle?.textTransform ?? undefined,
    fontWeight: style?.bodyStyle?.textDecoration?.isBold === true ? 'bold' : undefined,
    fontStyle: style?.bodyStyle?.textDecoration?.isItalic === true ? 'italic' : undefined,
    textDecoration:
      style?.bodyStyle?.textDecoration?.isUnderline === true ? 'underline' : undefined,
  };
  return (
    <RendererContainer {...rest} style={cssProperties}>
      {content.map((node, index) => (
        <ElementComponent
          key={index}
          node={node}
          renderElement={effectiveRenderElement}
          renderText={renderText}
        />
      ))}
    </RendererContainer>
  );
};
